import { cn } from '@/lib/utils'

const HTML_PATTERN = /<\/?[a-z][\s\S]*>/i
const BLOCKED_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed'])
const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'h2',
  'h3',
  'h4',
  'hr',
  'i',
  'li',
  'ol',
  'p',
  'pre',
  'small',
  'span',
  'strong',
  'ul',
])
const COMMON_ATTRIBUTES = new Set(['title'])
const TAG_ATTRIBUTES: Record<string, Set<string>> = {
  a: new Set(['href', 'rel', 'target', 'title']),
}

function isSafeUrl(value: string): boolean {
  const trimmed = value.trim()
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) {
    return true
  }

  try {
    const url = new URL(trimmed, window.location.origin)
    return ['http:', 'https:', 'mailto:'].includes(url.protocol)
  }
  catch {
    return false
  }
}

function sanitizeAttributes(element: HTMLElement, tag: string) {
  const allowedForTag = TAG_ATTRIBUTES[tag] ?? new Set<string>()

  for (const attribute of Array.from(element.attributes)) {
    const name = attribute.name.toLowerCase()
    if (
      name.startsWith('on')
      || name === 'style'
      || (!COMMON_ATTRIBUTES.has(name) && !allowedForTag.has(name))
    ) {
      element.removeAttribute(attribute.name)
      continue
    }

    if (name === 'href' && !isSafeUrl(attribute.value)) {
      element.removeAttribute(attribute.name)
    }
  }

  if (tag === 'a' && element.getAttribute('href')) {
    element.setAttribute('target', '_blank')
    element.setAttribute('rel', 'noopener noreferrer')
  }
}

function sanitizeHtml(input: string): string {
  if (typeof document === 'undefined') {
    return ''
  }

  const htmlDocument = document.implementation.createHTMLDocument('')
  const template = htmlDocument.createElement('template')
  template.innerHTML = input

  const sanitizeNode = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.COMMENT_NODE) {
        child.remove()
        continue
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        continue
      }

      const element = child as HTMLElement
      const tag = element.tagName.toLowerCase()
      if (BLOCKED_TAGS.has(tag)) {
        element.remove()
        continue
      }

      sanitizeNode(element)
      if (!ALLOWED_TAGS.has(tag)) {
        const fragment = htmlDocument.createDocumentFragment()
        while (element.firstChild) {
          fragment.append(element.firstChild)
        }
        element.replaceWith(fragment)
        continue
      }

      sanitizeAttributes(element, tag)
    }
  }

  sanitizeNode(template.content)
  return template.innerHTML
}

type ForumBodyProps = {
  body: string
  className?: string
  compact?: boolean
}

export function ForumBody({
  body,
  className,
  compact = false,
}: ForumBodyProps) {
  if (!HTML_PATTERN.test(body)) {
    return (
      <p
        className={cn(
          'whitespace-pre-wrap break-words text-foreground',
          className,
        )}
      >
        {body}
      </p>
    )
  }

  const sanitized = sanitizeHtml(body)
  if (!sanitized.trim()) {
    return (
      <p
        className={cn(
          'whitespace-pre-wrap break-words text-foreground',
          className,
        )}
      >
        {body}
      </p>
    )
  }

  return (
    <div
      className={cn(
        'prose prose-slate max-w-none break-words text-foreground dark:prose-invert',
        compact ? 'prose-sm' : 'prose-base',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  )
}
